import { useState, useCallback, useRef, useEffect } from "react";

interface AiAdvisorPanelProps {
  currentScript: string;
  onApplyScript: (script: string) => void;
}

interface Message {
  role: "user" | "assistant";
  content: string;
}

function extractCode(text: string): string {
  // Extract code from markdown code block if present
  const match = text.match(/```(?:javascript|js)?\n?([\s\S]*?)```/);
  if (match) return match[1].trim();
  // If the text starts with const/function/import, treat whole thing as code
  if (/^(const |function |import |let |var )/.test(text.trim())) return text.trim();
  return text.trim();
}

export default function AiAdvisorPanel({ currentScript, onApplyScript }: AiAdvisorPanelProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamText, setStreamText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamText]);

  const handleSend = useCallback(async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: prompt }]);
    setLoading(true);
    setStreamText("");

    try {
      const res = await fetch("/api/ai/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt, currentScript: currentScript || undefined }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${err.error || res.statusText}` }]);
        setLoading(false);
        return;
      }

      // Stream response
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        full += chunk;
        setStreamText(full);
      }

      setStreamText("");
      setMessages((prev) => [...prev, { role: "assistant", content: full }]);
    } catch (err: any) {
      setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${err.message}` }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, currentScript]);

  const handleApply = useCallback((content: string) => {
    const code = extractCode(content);
    onApplyScript(code);
  }, [onApplyScript]);

  return (
    <div className="ai-advisor-panel">
      <div className="ai-advisor-messages" ref={scrollRef}>
        {messages.length === 0 && !streamText && (
          <div className="ai-advisor-empty">
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="0.8" width="28" height="28" opacity="0.25">
              <rect x="4" y="4" width="24" height="20" rx="3"/>
              <path d="M10 12h12M10 16h8"/>
              <circle cx="24" cy="24" r="6" fill="currentColor" opacity="0.15"/>
              <path d="M22 24l2 2 3-3" strokeWidth="1.5"/>
            </svg>
            <span>AI Advisor</span>
            <span className="ai-advisor-hint">Describe a shape and get replicad code generated automatically</span>
            <div className="ai-advisor-examples">
              <button className="ai-advisor-example" onClick={() => setInput("Create a gear with 12 teeth")} title="Try this example prompt">gear with 12 teeth</button>
              <button className="ai-advisor-example" onClick={() => setInput("Make an L-bracket with mounting holes")} title="Try this example prompt">L-bracket with holes</button>
              <button className="ai-advisor-example" onClick={() => setInput("Create a round container with a lid")} title="Try this example prompt">container with lid</button>
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`ai-advisor-msg ai-advisor-msg--${msg.role}`}>
            {msg.role === "assistant" && (
              <div className="ai-advisor-msg-header">
                <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10" opacity="0.5">
                  <circle cx="8" cy="8" r="6"/>
                </svg>
                Claude
              </div>
            )}
            <pre className="ai-advisor-msg-content">{msg.content}</pre>
            {msg.role === "assistant" && !msg.content.startsWith("Error") && (
              <button className="ai-advisor-apply-btn" onClick={() => handleApply(msg.content)}>
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="11" height="11">
                  <path d="M4 8l3 3 5-6"/>
                </svg>
                Apply to editor
              </button>
            )}
          </div>
        ))}
        {streamText && (
          <div className="ai-advisor-msg ai-advisor-msg--assistant">
            <div className="ai-advisor-msg-header">
              <svg viewBox="0 0 16 16" fill="currentColor" width="10" height="10" opacity="0.5">
                <circle cx="8" cy="8" r="6"/>
              </svg>
              Claude
            </div>
            <pre className="ai-advisor-msg-content">{streamText}<span className="ai-advisor-cursor">|</span></pre>
          </div>
        )}
      </div>

      <form className="ai-advisor-input-bar" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
        <input
          className="ai-advisor-input"
          type="text"
          placeholder="Describe a shape..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <button className="ai-advisor-send-btn" type="submit" disabled={loading || !input.trim()} title="Send prompt to AI">
          {loading ? (
            <svg className="ai-advisor-spinner" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <circle cx="8" cy="8" r="6" strokeDasharray="30" strokeDashoffset="10"/>
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <path d="M2 2l12 6-12 6V9l8-1-8-1V2z"/>
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
